/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Typography } from '@mui/material';
import type { Feature } from 'geojson';
import React, { useMemo } from 'react';
import { CustomAccordion } from '@opengeoweb/shared';
import { useCoreTranslation } from '../../utils/i18n';

export interface SelectedFeatureProps {
  feature: Feature;
  layerName: string;
}

const textStyle = {
  lineHeight: '20px',
  letterSpacing: '0.25px',
};

const textStyleWithMargin = {
  ...textStyle,
  marginBottom: 1,
};

const SelectedFeature: React.FC<SelectedFeatureProps> = ({
  feature,
  layerName,
}: SelectedFeatureProps) => {
  const { t } = useCoreTranslation();

  const getFormattedCoordinates = (lat: number, lon: number): string => {
    const latHemisphere = lat >= 0 ? 'N' : 'S';
    const lonHemisphere = lon >= 0 ? 'E' : 'W';

    return `${Math.abs(lat)}° ${latHemisphere}, ${Math.abs(lon)}° ${lonHemisphere}`;
  };

  const formattedCoordinates = useMemo(() => {
    if (feature?.geometry.type === 'Point') {
      const latitude = feature.geometry.coordinates[1];
      const longitude = feature.geometry.coordinates[0];

      return getFormattedCoordinates(latitude, longitude);
    }
    return '';
  }, [feature]);

  const featureInfoKeys = useMemo(() => {
    return feature
      ? Object.keys(feature.properties || {})
          .filter(
            (key) =>
              ![
                'id',
                'geometry',
                'hover',
                'highlightSelectedFeature',
                'highlightNearbyFeature',
              ].includes(key),
          )
          .sort()
      : [];
  }, [feature]);

  return (
    <CustomAccordion key={layerName} title={layerName}>
      <Box sx={{ minWidth: '12rem', paddingX: '1rem' }}>
        <Typography color="textSecondary" sx={textStyle} variant="caption">
          {t('featureinfo-id')}
        </Typography>
        <Typography sx={textStyleWithMargin} variant="body2">
          {feature.id}
        </Typography>
        <Typography color="textSecondary" sx={textStyle} variant="caption">
          {t('featureinfo-location')}
        </Typography>
        <Typography sx={textStyleWithMargin} variant="body2">
          {formattedCoordinates}
        </Typography>
        {featureInfoKeys.map((key) => (
          <React.Fragment key={key}>
            <Typography color="textSecondary" sx={textStyle} variant="caption">
              {t(`${key}`)}
            </Typography>
            <Typography sx={textStyleWithMargin} variant="body2">
              {feature.properties?.[key]?.toString()}
            </Typography>
          </React.Fragment>
        ))}
      </Box>
    </CustomAccordion>
  );
};

export default SelectedFeature;
